import { ReactQueryStaleTime } from '@openx/constants/react-query.ts';
import { OptionsType } from '@openx/types/options.ts';
import type {
  TargetingBlockingRulesSchema,
  UseTargetingBlockingRulesSchemaFetch,
} from '@openx/types/targeting/targetingProviderConfig.ts';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { isObjectRecord } from '../lib/typeGuards/isObjectRecord.ts';
import { fetchOptions } from '../state/options/api.ts';

const isSchemaLike = (value: unknown): value is TargetingBlockingRulesSchema =>
  isObjectRecord(value) && ('$schema' in value || 'definitions' in value || 'allOf' in value);

const extractSchema = (response: unknown): TargetingBlockingRulesSchema | null => {
  if (!Array.isArray(response)) {
    return null;
  }

  const firstItem = response[0];

  if (isSchemaLike(firstItem)) {
    return firstItem;
  }

  if (isObjectRecord(firstItem)) {
    const extra = firstItem.extra;

    if (isSchemaLike(extra)) {
      return extra;
    }

    if (Array.isArray(extra)) {
      return extra.find(isSchemaLike) ?? null;
    }
  }

  return null;
};

export const useBlockIrrelevantTargetingSchemaFetch: UseTargetingBlockingRulesSchemaFetch = ({
  version = '1',
  enabled = true,
} = {}) => {
  const { t } = useTranslation();

  const { data, isLoading, refetch } = useQuery<TargetingBlockingRulesSchema | null>({
    enabled,
    meta: {
      errorMessage: t('Failed to fetch targeting blocking rules schema'),
    },
    queryFn: async () => {
      const response = await fetchOptions(OptionsType.BLOCK_IRRELEVANT_TARGETING_SCHEMA, { version });

      return extractSchema(response);
    },
    queryKey: [OptionsType.BLOCK_IRRELEVANT_TARGETING_SCHEMA, version],
    staleTime: ReactQueryStaleTime.MINUTES_15,
  });

  return {
    isTargetingBlockingRulesSchemaLoading: isLoading,
    refetchTargetingBlockingRulesSchema: refetch,
    targetingBlockingRulesSchema: data,
  };
};
